import DynamicForm, { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { forwardRef, ReactNode, useEffect, useRef, useState } from "react";
import SalesByClientFormConfig from "./form";
import { Button, Col, Row, Table } from "reactstrap";
import { SalesByClientFormDataType } from "@/formTypes/SalesByClientFormDataType";
import moment from "moment";
import { showAlert } from "@utils/Alert";
import { ClientApi, ClientDataType } from "@/api/ClientApi";
import LoadingScreen from "@components/content/LoadingScreen";
import { formatCant } from "@utils/Cant";
import { CURRENCIES_CODE_IDS } from "@utils/Constants";
import { formatMoneyValue } from "@utils/Currency";
import { ALL_SALEMEN_OPTION, fetchSalesByClientProducts, SalesByClientReportResult } from "@utils/SalesByClientReport";

type SalesByClientFormProps = {
}

type SalesByClientReportFilters = {
  clientId: string,
  salemanId: string,
  start: string,
  end: string,
  groupByCategory: boolean
}

const EMPTY_REPORT: SalesByClientReportResult = { rows: [], totalCant: 0, totalAmount: 0 };

const SalesByClientForm = forwardRef<DynamicFormRef, SalesByClientFormProps>((props, ref) => {
  const formRef = useRef<DynamicFormRef>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReportGenerated, setReportGenerated] = useState<boolean>(false);
  const [clients, setClients] = useState<ClientDataType[]>([]);
  const [report, setReport] = useState<SalesByClientReportResult>(EMPTY_REPORT)
  const [reportFilters, setReportFilters] = useState<SalesByClientReportFilters>()

  useEffect(() => {
    const endDate = moment().format("YYYY-MM-DD");
    const startDate = moment().subtract(1, "month").format("YYYY-MM-DD");

    formRef.current?.resetFormValue({
      start: startDate,
      end: endDate,
      salemanId: ALL_SALEMEN_OPTION,
      groupByCategory: false
    })
    loadClients()
  }, [])

  const loadClients = async () => {
    setIsLoading(true)
    try {
      const { items } = await ClientApi.fetchAll();
      setClients(items);
    } finally {
      setIsLoading(false)
    }
  }

  const getFormConfig = () => {
    return SalesByClientFormConfig;
  }

  const generateReport = async (formData: SalesByClientFormDataType) => {

    try {
      setIsLoading(true)

      const { end = "", start = "", clientId = "", salemanId = ALL_SALEMEN_OPTION, groupByCategory = false } = formData

      const reportResult = await fetchSalesByClientProducts({
        clientId,
        salemanId,
        start,
        end,
        groupByCategory: !!groupByCategory
      })

      setReportFilters({ clientId, salemanId, start, end, groupByCategory: !!groupByCategory })
      setReport(reportResult)
      setReportGenerated(true)
    } finally {
      setIsLoading(false)
    }
  }

  const openReport = (format: "print" | "excel") => {
    if (!reportFilters) {
      return;
    }

    const { clientId, salemanId, start, end, groupByCategory } = reportFilters;
    const groupFlag = groupByCategory ? "1" : "0";
    window.open(`/user/reports/${format}/sales-by-clients/${clientId}/${salemanId}/${start}/${end}/${groupFlag}`, "_blank")
  }

  const isGrouped = (): boolean => {
    return !!reportFilters?.groupByCategory;
  }

  const renderSaleProducts = (): ReactNode[] => {
    return report.rows.map(r => {

      if (isGrouped()) {
        return (<tr key={r.key}>
          <td>{r.description}</td>
          <td>{formatCant(r.totalCant)}</td>
          <td>{formatMoneyValue(r.totalAmount, CURRENCIES_CODE_IDS.CRC)}</td>
        </tr>)
      }

      return (<tr key={r.key}>
        <td>{r.codigo || "Temporal"}</td>
        <td>{r.codigo_cabys || "Temporal"}</td>
        <td>{r.description || "Temporal"}</td>
        <td>{formatCant(r.totalCant)}</td>
        <td>{formatMoneyValue(r.totalAmount, CURRENCIES_CODE_IDS.CRC)}</td>
      </tr>)
    })
  }

  const onInputKeyDown = (name: keyof SalesByClientFormDataType | "client_code", event?: React.KeyboardEvent<any>) => {
    if (name === "client_code") {
      if (event?.key == "Enter") {
        event?.preventDefault();
        event?.stopPropagation();
        const input = event.target as HTMLInputElement
        selectClientByCode(input.value)
      }
    }
  }

  const selectClientByCode = async (code: string) => {
    const client = clients.find(c => c.codigo === code);
    if (client?.id) {
      formRef.current?.setFormValue("clientId", client.id, "String");
    } else {
      showAlert("danger", "No existe ningun cliente con ese codigo");
    }
  }


  return (
    <>
      <p>
        Por favor seleccione un Cliente y/o un Vendedor para generar el reporte de historial de ventas
      </p>
      <DynamicForm<SalesByClientFormDataType>
        ref={formRef}
        id="report"
        onInputKeyDown={onInputKeyDown}
        formConfig={getFormConfig()}
        onSubmit={generateReport}
      >
        <Row>
          <Col md={{
            offset: 2
          }}>
            <Button className="me-3" type="submit" color="primary" size="lg">Generar Reporte</Button>
          </Col>
        </Row>
      </DynamicForm>

      {report.rows.length > 0 && (
        <div>
          <Row className="mt-3">
            <Col>
              <Button
                className="me-3"
                type="button"
                color="secondary"
                data-testid="print-sales-by-client"
                onClick={() => openReport("print")}
              >
                Imprimir
              </Button>
              <Button
                type="button"
                color="success"
                data-testid="excel-sales-by-client"
                onClick={() => openReport("excel")}
              >
                Exportar a Excel
              </Button>
            </Col>
          </Row>
          <Table className="mt-3">
            <thead>
              <tr>
                {isGrouped() ? (
                  <th>Categoria</th>
                ) : (
                  <>
                    <th>Codigo</th>
                    <th>Codigo Cabys</th>
                    <th>Descripcion</th>
                  </>
                )}
                <th>Cantidad</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {renderSaleProducts()}
            </tbody>
            <tfoot>
              <tr data-testid="sales-by-client-total">
                <th colSpan={isGrouped() ? 1 : 3}>Total general</th>
                <th>{formatCant(report.totalCant)}</th>
                <th>{formatMoneyValue(report.totalAmount, CURRENCIES_CODE_IDS.CRC)}</th>
              </tr>
            </tfoot>
          </Table>
        </div>
      )}
      {isReportGenerated && report.rows.length == 0 ? "No hay datos disponibles para crear el report" : ""}

      <LoadingScreen isLoading={isLoading} />
    </>
  )
}
);

export default SalesByClientForm;
